import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../context/StoreContext';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';import { collection, query, where, getDocs, addDoc, serverTimestamp, doc, setDoc } from 'firebase/firestore';

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
        style: 'currency', currency: 'KES', minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(amount);
};

const Checkout = () => {
    const { cart, clearCart } = useStore();
    const { currentUser } = useAuth();
    const navigate = useNavigate();


    const [cartItemsDetails, setCartItemsDetails] = useState([]);
    const [loadingPrices, setLoadingPrices] = useState(true);
    const [loadingPayment, setLoadingPayment] = useState(false);
    // const [waitingForPin, setWaitingForPin] = useState(false); // Commented out
    // const [checkoutRequestId, setCheckoutRequestId] = useState(null); // Commented out
    const [paymentStatus, setPaymentStatus] = useState('TILL_NUMBER_ONLY'); // New status
    const [manualMpesaMessage, setManualMpesaMessage] = useState('');
    const [manualVerificationLoading, setManualVerificationLoading] = useState(false);
    const [manualVerificationError, setManualVerificationError] = useState(null);

    const [shippingDetails, setShippingDetails] = useState({
        name: currentUser ? currentUser.email.split('@')[0] : '',
        address: '',
        city: 'Nairobi',
        phone: '',
    });

    const handleManualVerification = async () => {
        setManualVerificationLoading(true);
        setManualVerificationError(null);

        try {
            // --- SAVE ORDER TO FIRESTORE WITH PENDING STATUS ---
            const ordersCollectionRef = collection(db, 'orders');
            await addDoc(ordersCollectionRef, {
                userId: currentUser.uid, // Revert to using uid as userId
                customerEmail: currentUser.email,
                shippingDetails: shippingDetails,
                items: cartItemsDetails,
                totalAmount: orderTotal,
                paymentMethod: 'M-Pesa Till Number (Manual)',
                paymentStatus: 'pending', // Set status to pending
                orderDate: serverTimestamp(),
            });

            // --- CREATE/UPDATE USER'S PROFILE WITH PHONE NUMBER ---
            const userRef = doc(db, 'users', currentUser.uid);
            await setDoc(userRef, { phone: shippingDetails.phone }, { merge: true });

            clearCart();
            navigate('/order-success');
        } catch (error) {
            console.error("Error creating order:", error);
            setManualVerificationError("An error occurred while creating the order.");
        }
        setManualVerificationLoading(false);
    };

    useEffect(() => {
        const fetchProductDetails = async () => {
            if (cart.length === 0) {
                setCartItemsDetails([]);
                setLoadingPrices(false);
                return;
            }
            try {
                const productIds = cart.map(item => item.id);
                const productsRef = collection(db, 'products');
                const q = query(productsRef, where('__name__', 'in', productIds));
                const querySnapshot = await getDocs(q);
                const detailsMap = {};
                querySnapshot.forEach(doc => { detailsMap[doc.id] = doc.data(); });

                const mergedItems = cart.map(item => ({
                    ...item,
                    ...detailsMap[item.id],
                    price: detailsMap[item.id]?.price || 0
                }));
                setCartItemsDetails(mergedItems);
            } catch (error) {
                console.error("Error fetching prices:", error);
                setCartItemsDetails(cart);
            } finally {
                setLoadingPrices(false);
            }
        };
        fetchProductDetails();
    }, [cart]);

    // Comment out the useEffect for STK push payment status
    /*
    useEffect(() => {
        if (waitingForPin && checkoutRequestId) {
            const interval = setInterval(async () => {
                try {
                    const checkPayment = httpsCallable(functions, 'checkMpesaPayment');
                    const response = await checkPayment({ checkoutRequestId });
                    if (response.data.status === 'PAID') {
                        setPaymentStatus('PAID');
                        clearInterval(interval);
                        clearCart();
                        navigate('/order-success');
                    }
                } catch (error) {
                    console.error("Error checking payment status:", error);
                }
            }, 5000);

            const timeout = setTimeout(() => {
                clearInterval(interval);
                if (paymentStatus !== 'PAID') {
                    setPaymentStatus('TIMEOUT');
                }
            }, 120000); // 2 minutes timeout

            return () => {
                clearInterval(interval);
                clearTimeout(timeout);
            };
        }
    }, [waitingForPin, checkoutRequestId, functions, navigate, clearCart]);
    */

    const subtotal = cartItemsDetails.reduce((total, item) => total + (item.price * item.quantity), 0);
    const shippingCost = 1;
    const orderTotal = subtotal + shippingCost;

    
    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setShippingDetails(prev => ({ ...prev, [name]: value }));
    };

    const handlePlaceOrder = async (e) => {
        e.preventDefault();
        setLoadingPayment(true); // Still show loading while "processing" order

        // --- Temporarily disable STK push ---
        // For now, just simulate a successful "order placement" and
        // immediately direct them to the till number display.

        // In a real scenario, you might save the order to Firestore here
        // with a 'pending' status for manual M-Pesa verification.

        // Simulate a slight delay for better UX
        await new Promise(resolve => setTimeout(resolve, 1500));

        setLoadingPayment(false);
        // Directly show till number instructions
        setPaymentStatus('TILL_NUMBER_ONLY');
        // setWaitingForPin(false); // No longer relevant
    };

    if (loadingPrices) return <div className="container mx-auto text-center py-20"><h2>Loading...</h2></div>;
    if (cart.length === 0) return <div className="container mx-auto text-center py-20"><h1>Cart Empty</h1></div>;

    return (
        <div className="container mx-auto px-4 py-8">
            <h1 className="text-3xl font-bold text-center mb-8">Checkout</h1>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                <div className="bg-white p-8 rounded-lg shadow-md">
                    <h2 className="text-2xl font-bold mb-6">Shipping Details</h2>
                    <form onSubmit={handlePlaceOrder} className="space-y-4">
                        <div>
                            <label className="block font-medium text-gray-700">Full Name</label>
                            <input type="text" name="name" value={shippingDetails.name} onChange={handleFormChange} required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"/>
                        </div>
                        <div>
                            <label className="block font-medium text-gray-700">Address</label>
                            <input type="text" name="address" value={shippingDetails.address} onChange={handleFormChange} required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"/>
                        </div>
                        <div>
                            <label className="block font-medium text-gray-700">City</label>
                            <select name="city" value={shippingDetails.city} onChange={handleFormChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"><option>Nairobi</option></select>
                        </div>
                        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                            <label className="block font-bold text-green-800">M-Pesa Phone Number</label>
                            <input
                                type="tel"
                                name="phone"
                                value={shippingDetails.phone}
                                onChange={handleFormChange}
                                placeholder="e.g. 0712 345 678"
                                required
                                className="mt-1 block w-full rounded-md border-green-400 shadow-sm focus:border-green-500 focus:ring-green-500"
                            />
                            <small className="text-green-700">Enter the number that will pay.</small>
                        </div>

                        {!loadingPayment && paymentStatus === 'TILL_NUMBER_ONLY' ? (
                            <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded-md">
                                <p className="font-bold">Please pay directly to our M-Pesa Till Number:</p>
                                <p className="text-3xl font-extrabold mt-2">6663498</p>
                                <p className="mt-2 text-sm">After paying, please verify your payment below:</p>
                                <div className="mt-4">
                                    <p className="text-gray-600 mb-2">Paste M-Pesa confirmation message:</p>
                                    <textarea
                                        className="w-full p-2 border rounded text-gray-800"
                                        rows="3"
                                        value={manualMpesaMessage}
                                        onChange={(e) => setManualMpesaMessage(e.target.value)}
                                        placeholder="E.g., 'Confirm you have sent KES 100.00 to LUMIO TECHNOLOGIES...'"
                                    />
                                    <button
                                        onClick={handleManualVerification}
                                        className="bg-blue-600 text-white px-4 py-2 rounded-md mt-2 hover:bg-blue-700"
                                        disabled={manualVerificationLoading}
                                        type="button" // Important to prevent form submission
                                    >
                                        {manualVerificationLoading ? 'Verifying...' : 'Verify Payment'}
                                    </button>
                                    {manualVerificationError && <p className="text-red-500 mt-2">{manualVerificationError}</p>}
                                </div>
                            </div>
                        ) : (
                            <button type="submit" className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 font-bold text-lg disabled:bg-gray-400" disabled={loadingPayment}>
                                {loadingPayment ? 'Processing Order...' : `Proceed to Payment`}
                            </button>
                        )}
                    </form>
                </div>

                <div className="bg-white p-8 rounded-lg shadow-md">
                    <h2 className="text-2xl font-bold mb-6">Order Summary</h2>
                    <div className="space-y-2">
                        {cartItemsDetails.map(item => (
                            <div key={item.id} className="flex justify-between">
                                <span>{item.name} x{item.quantity}</span>
                                <span>{formatCurrency((item.price || 0) * item.quantity)}</span>
                            </div>
                        ))}
                    </div>
                    <hr className="my-4"/>
                    <div className="flex justify-between font-bold text-xl">
                        <span>Total:</span>
                        <span>{formatCurrency(orderTotal)}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Checkout;